/* eslint-disable no-console */
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';

// Utils
import { orderBy } from 'lodash';
// components
import { Avatar } from '@repo/zolintra-ui';

export const RESOURCES_POSITIONS = {
  ADMINISTRATION_INTERNE: 'ADMINISTRATION_INTERNE',
  COMMUNICATION: 'COMMUNICATION',
  DEV_BACK: 'DEV_BACK',
  DEV_FRONT: 'DEV_FRONT',
  DEV_OPS: 'DEV_OPS',
  DIRECTION_COMMERCIALE: 'DIRECTION_COMMERCIALE',
  DIRECTION_GENERALE: 'DIRECTION_GENERALE',
  DIRECTION_TECHNIQUE: 'DIRECTION_TECHNIQUE',
  PILOTAGE_PRODUIT: 'PILOTAGE_PRODUIT',
  QWERI_DATA_MARKETING: 'QWERI_DATA_MARKETING',
  QWERI_INGENIEUR_DATA: 'QWERI_INGENIEUR_DATA',
  QWERI_WEB_ANALYST: 'QWERI_WEB_ANALYST',
  TRANSFORMATION_INNOVATION: 'TRANSFORMATION_INNOVATION',
  UX_UI: 'UX_UI',
};

const useResources = () => {
  const { t } = useTranslation();

  /**
   * Get a formatted name to display based on firstname and lastname
   *
   * @param {import('../../services/types').Resource} resource
   * @param {boolean} firstNameOnly
   * @returns {string}
   */
  const getResourceDisplayName = (resource, firstNameOnly = false) => {
    if (!resource?.firstName || !resource?.lastName) return t('common.userNotFound');
    return firstNameOnly ? resource.firstName : `${resource.firstName} ${resource.lastName}`;
  };

  /**
   * Get resource position to display
   *
   * @param {import('../../services/types').Resource} resource
   * @returns {string}
   */
  const getResourcePosition = (resource) => {
    if (!resource?.position) return null;

    if (RESOURCES_POSITIONS[resource.position] === undefined) {
      console.warn(`This resource position does not exist in Google console : ${resource.position}`);
      Sentry.captureException(new Error(`This resource position does not exist in Google console : ${resource.position}`));
      return resource.position;
    }

    return t(`positions.${RESOURCES_POSITIONS[resource.position]}`);
  };

  /**
   * Transforms a list resources into a format suitable for use as
   * options in a select dropdown.
   *
   * @param {Object} resourcesList
   * @returns {Array<{label:string, value:string, icon:object}>}
   */
  const transformResourcesListToOptions = (resourcesList) => {
    const optionsList = Object.values(resourcesList).reduce(
      (acc, resource) => (
        acc.concat({
          label: getResourceDisplayName(resource),
          value: resource.id,
          icon: resource.photo ? (
            <Avatar size="small" src={resource.photo} title={getResourceDisplayName(resource)} />
          ) : null,
        })
      ),
      [],
    );
    return orderBy(optionsList, ['label']);
  };

  /**
   * Takes the resources list from staff-management API and put resources from identity API
   * (with Google informations) inside every entry when there is a match
   *
   * @param {Array<Object>} staffList
   * @param {Array<Object>} usersList
   * @returns {Array<Object>}
   */
  const getStaffResourcesListWithUserInfo = (staffList, usersList) => (
    staffList?.map((resource) => ({
      ...resource,
      ...usersList?.find((user) => user.id === resource.id) || [],
    }))
    || []
  );

  return {
    getResourceDisplayName,
    getResourcePosition,
    transformResourcesListToOptions,
    getStaffResourcesListWithUserInfo,
  };
};

export default useResources;
